import { useCallback, useEffect, type ClipboardEventHandler } from 'react';

export function triggerUpload(selector: string, file: File) {
  const uploader = document.querySelector<HTMLInputElement>(selector);

  if (!uploader) {
    return false;
  }

  const dataTransfer = new DataTransfer();
  dataTransfer.items.add(file);
  uploader.files = dataTransfer.files;
  uploader.dispatchEvent(
    new Event('change', {
      bubbles: true,
    }),
  );

  return true;
}

export function usePaste(
  selector: string,
  options?: { filter?: (file: File) => boolean },
) {
  const onPaste: ClipboardEventHandler = useCallback(
    (ev) => {
      const item = ev.clipboardData.files.item(0);

      if (!item) {
        return;
      }

      if (options?.filter && !options.filter(item)) {
        return;
      }

      if (triggerUpload(selector, item)) {
        ev.preventDefault();
        return;
      }
    },
    [options?.filter],
  );

  return onPaste;
}

export function useAutoPaste(
  selector: string,
  options?: {
    filter?: (file: File) => boolean;
    onPaste?: ClipboardEventHandler;
  },
) {
  const onPaste = usePaste(selector, options);

  useEffect(() => {
    const cb: ClipboardEventHandler = (ev) => {
      if (options?.onPaste) {
        options.onPaste(ev);
        return;
      }

      onPaste(ev);
    };

    document.addEventListener('paste', cb as any);

    return () => {
      document.removeEventListener('paste', cb as any);
    };
  }, [onPaste]);
}
